/**
 * Temporal Parking Test Page
 * Standalone page for testing temporal parking algorithm with visual feedback
 */

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { DecisionLogDisplay } from "./components/DecisionLogDisplay";
import { ParkingConfigForm } from "./components/ParkingConfigForm";
import { PlacementList } from "./components/PlacementList";
import { TimelineView } from "./components/TimelineView";
import { countTrappedAircraft } from "./egress";
import { precomputeAllEgressMasks } from "./egressMasks";
import { useParkingConfig } from "./hooks/useParkingConfig";
import { planParkingAssignments } from "./temporalPlanner";
import { getFreshTestData, TEST_SCENARIOS } from "./testData";
import { PlanningResultWithData } from "./types";

export const TemporalTest: React.FC = () => {
  const { t } = useTranslation();
  const [result, setResult] = React.useState<PlanningResultWithData | null>(
    null
  );
  const [selectedScenario, setSelectedScenario] = React.useState("basic");
  const [bufferMinutes, setBufferMinutes] = React.useState(15);
  const [plannedMoveCap, setPlannedMoveCap] = React.useState(0);
  const [isRunning, setIsRunning] = React.useState(false);

  // Use shared parking config hook
  const parkingConfig = useParkingConfig({
    bufferMinutes: bufferMinutes,
    plannedMoveCap: plannedMoveCap,
    allowTrapping: true,
    egressWeight: 1.0,
    wasteWeight: 1.0,
    structuralWeight: 0.1,
    sortingStrategy: "arrival",
    zonePriorityWeight: 1.0,
    min_dwell_weight: 2.0,
    arrivalStagingDwellMinutes: 45,
    departureStagingDwellMinutes: 45,
    quickTurnThresholdMinutes: 90,
  });

  const currentScenario =
    TEST_SCENARIOS.find((s) => s.id === selectedScenario) || TEST_SCENARIOS[0];

  // Reset results when parameters change
  React.useEffect(() => {
    setResult(null);
  }, [selectedScenario, bufferMinutes, plannedMoveCap, parkingConfig.config]);

  const handleRunPlanner = () => {
    setIsRunning(true);

    // Get fresh test data (resets bitmaps)
    const { aircraft, spots, zones, horizonHours, startTime } =
      getFreshTestData(selectedScenario);

    // Run the planner with current settings
    const planningResult = planParkingAssignments(
      aircraft,
      spots,
      bufferMinutes,
      plannedMoveCap,
      parkingConfig.config.allowTrapping,
      parkingConfig.config.egressWeight,
      parkingConfig.config.wasteWeight,
      parkingConfig.config.structuralWeight,
      parkingConfig.config.sortingStrategy,
      horizonHours,
      true, // useMaskOptimization - Phase 9 fast path
      false, // validateMasks - validation not needed (proven correct). TODO: remove validation parameter entirely in future
      parkingConfig.config.zonePriorityWeight,
      zones,
      parkingConfig.config.min_dwell_weight
    );

    // Precompute egress masks for visualization and analysis
    // WHY: Allows fast egress blocking checks and powers the blocked spot visualization on timeline
    // WHEN: After placement complete, before rendering results
    precomputeAllEgressMasks(
      spots,
      planningResult.placement,
      aircraft,
      bufferMinutes
    );

    // Calculate trap statistics for display in stats panel
    // WHY: Users need high-level overview of how many egress conflicts exist
    // USE CASE: "3 aircraft are blocking 8 others" tells operator there's a problem
    let trappingCount = 0; // How many aircraft are creating traps
    let totalTrappedAircraft = 0; // How many aircraft are trapped (sum)

    for (const aircraftId in planningResult.placement) {
      const ac = aircraft.find((a) => a.id === aircraftId);
      const placementInfo = planningResult.placement[aircraftId];

      // Skip segmented placements for now
      // WHY: Trap detection for segments needs special handling (future enhancement)
      if (!ac || !("spotId" in placementInfo)) continue;

      const spot = spots.find((s) => s.id === placementInfo.spotId);
      if (!spot) continue;

      // Check how many aircraft this placement traps
      // WHY: Some placements trap 0, some trap many - we need to count each
      const trapCount = countTrappedAircraft(
        ac, // Aircraft we placed
        spot, // Where we placed it
        planningResult.placement, // All placements
        aircraft, // All aircraft (for look-ahead)
        spots // All spots (for blocking)
      );

      // If this aircraft traps at least one other, count it
      // WHY: trappingCount = "problematic placements", totalTrappedAircraft = "total impact"
      if (trapCount > 0) {
        trappingCount++; // One more problematic placement
        totalTrappedAircraft += trapCount; // Add to total impact
      }
    }

    // Store everything together in state with trap statistics
    setResult({
      ...planningResult,
      stats: {
        ...planningResult.stats,
        trappingCount,
        totalTrappedAircraft,
      },
      aircraft,
      spots,
      zones,
      horizonHours,
      startTime,
    });

    setIsRunning(false);
  };

  // Check if results match expectations
  const resultsMatch =
    result &&
    result.stats.placed === currentScenario.expectedPlaced &&
    result.stats.unplaced === currentScenario.expectedUnplaced;

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        {t('screens.ramp.temporalTest.index.title')}
      </Typography>

      <Stack spacing={1.5}>
        {/* Core Settings Row */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: 300 }} size="small">
            <InputLabel>{t('screens.ramp.temporalTest.index.testScenario')}</InputLabel>
            <Select
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              label={t('screens.ramp.temporalTest.index.testScenario')}
            >
              {TEST_SCENARIOS.map((scenario) => (
                <MenuItem key={scenario.id} value={scenario.id}>
                  {scenario.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ParkingConfigForm
            {...parkingConfig.config}
            onBufferMinutesChange={(value) => {
              setBufferMinutes(value);
              parkingConfig.setBufferMinutes(value);
            }}
            onPlannedMoveCapChange={(value) => {
              setPlannedMoveCap(value);
              parkingConfig.setPlannedMoveCap(value);
            }}
            onAllowTrappingChange={parkingConfig.setAllowTrapping}
            onEgressWeightChange={parkingConfig.setEgressWeight}
            onWasteWeightChange={parkingConfig.setWasteWeight}
            onStructuralWeightChange={parkingConfig.setStructuralWeight}
            onZonePriorityWeightChange={parkingConfig.setZonePriorityWeight}
            onMinDwellWeightChange={parkingConfig.setMinDwellWeight}
            onSortingStrategyChange={parkingConfig.setSortingStrategy}
            onArrivalStagingDwellMinutesChange={parkingConfig.setArrivalStagingDwellMinutes}
            onDepartureStagingDwellMinutesChange={parkingConfig.setDepartureStagingDwellMinutes}
            onQuickTurnThresholdMinutesChange={parkingConfig.setQuickTurnThresholdMinutes}
            size="small"
            layout="row"
          />

          <Button
            data-testid="run-planner-button"
            variant="contained"
            color="primary"
            onClick={handleRunPlanner}
            disabled={isRunning}
            startIcon={<PlayArrowIcon />}
          >
            {isRunning ? t('screens.ramp.temporalTest.index.buttons.running') : t('screens.ramp.temporalTest.index.buttons.runPlanner')}
          </Button>
        </Stack>

        {result && (
          <Paper
            data-testid="stats"
            elevation={1}
            sx={{
              p: 1,
              bgcolor: resultsMatch ? "success.50" : "warning.50",
              borderColor: resultsMatch ? "success.200" : "warning.200",
              display: "inline-flex",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              flexWrap="wrap"
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="subtitle2">{t('screens.ramp.temporalTest.index.stats.planningStatistics')}</Typography>
                {resultsMatch ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <WarningIcon color="warning" fontSize="small" />
                )}
              </Stack>
              <Typography variant="body2">
                <strong>{t('screens.ramp.temporalTest.index.stats.totalAircraft')}</strong> {result.stats.totalAircraft}
              </Typography>
              <Typography variant="body2" color="success.main">
                <strong>{t('screens.ramp.temporalTest.index.stats.placed')}</strong> {result.stats.placed}
              </Typography>
              <Typography variant="body2" color="error.main">
                <strong>{t('screens.ramp.temporalTest.index.stats.unplaced')}</strong> {result.stats.unplaced}
              </Typography>
              {result.stats.plannedMoves > 0 && (
                <Typography variant="body2" color="info.main">
                  <strong>{t('screens.ramp.temporalTest.index.stats.plannedMoves')}</strong> {result.stats.plannedMoves}
                </Typography>
              )}
              {/* Show trap statistics if any traps exist
                  WHY: Operators need to know if egress blocking is a problem
                  EXAMPLE: "Traps: 2 aircraft blocking 5 others" → operator knows to investigate
                  COLOR: Warning (yellow/orange) because traps are concerning but not failures */}
              {result.stats.trappingCount !== undefined &&
                result.stats.trappingCount > 0 && (
                  <Typography variant="body2" color="warning.main">
                    <strong>{t('screens.ramp.temporalTest.index.stats.traps')}</strong> {result.stats.trappingCount}{" "}
                    {t('screens.ramp.temporalTest.index.stats.aircraftBlocking')} {result.stats.totalTrappedAircraft} {t('screens.ramp.temporalTest.index.stats.others')}
                  </Typography>
                )}
              {/* Show "No Traps" when trappingCount === 0
                  WHY: Positive feedback that egress blocking is working correctly
                  USE CASE: After fix, seeing "No Traps ✓" confirms the issue is resolved */}
              {result.stats.trappingCount === 0 && result.stats.placed > 0 && (
                <Typography variant="body2" color="success.main">
                  <strong>{t('screens.ramp.temporalTest.index.stats.traps')}</strong> {t('screens.ramp.temporalTest.index.stats.none')}
                </Typography>
              )}
              <Typography
                variant="caption"
                color={resultsMatch ? "success.dark" : "warning.dark"}
              >
                {t('screens.ramp.temporalTest.index.stats.expected')} {currentScenario.expectedPlaced} {t('screens.ramp.temporalTest.index.stats.placedSlash')}{" "}
                {currentScenario.expectedUnplaced} {t('screens.ramp.temporalTest.index.stats.unplacedSuffix')}
                {resultsMatch ? t('screens.ramp.temporalTest.index.stats.matchCheck') : t('screens.ramp.temporalTest.index.stats.mismatch')}
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* Scenario Description */}
        <Typography variant="caption" color="text.secondary">
          {currentScenario.description}
        </Typography>

        {/* Instructions - Only show before running planner */}
        {!result && (
          <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "text.secondary", my: 1 }}
          >
            {t('screens.ramp.temporalTest.index.instructions')}
          </Typography>
        )}

        {/* Parameter Explanations - Only show before running planner */}
        {!result && (
          <Paper
            sx={{
              p: 2,
              bgcolor: "grey.50",
              border: 1,
              borderColor: "grey.300",
            }}
            elevation={0}
          >
            <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
              <strong>{t('screens.ramp.temporalTest.index.parameterGuide.title')}</strong>
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('screens.ramp.temporalTest.index.parameterGuide.buffer.title')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.buffer.description')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "grey.400",
                  }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.buffer.example')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('screens.ramp.temporalTest.index.parameterGuide.plannedMoves.title')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.plannedMoves.description')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "grey.400",
                  }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.plannedMoves.example')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('screens.ramp.temporalTest.index.parameterGuide.allowTrapping.title')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.allowTrapping.description')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "grey.400",
                  }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.allowTrapping.example')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('screens.ramp.temporalTest.index.parameterGuide.egressWeight.title')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.egressWeight.description')}
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.egressWeight.levels.low')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.egressWeight.levels.medium')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.egressWeight.levels.high')}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "grey.400",
                  }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.egressWeight.example')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('screens.ramp.temporalTest.index.parameterGuide.wasteWeight.title')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.wasteWeight.description')}
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.wasteWeight.levels.disabled')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.wasteWeight.levels.low')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.wasteWeight.levels.medium')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.wasteWeight.levels.high')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.wasteWeight.levels.veryHigh')}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "grey.400",
                  }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.wasteWeight.example')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('screens.ramp.temporalTest.index.parameterGuide.sortingStrategy.title')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.sortingStrategy.description')}
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sortingStrategy.strategies.arrivalOrder')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sortingStrategy.strategies.sizeConstrained')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sortingStrategy.strategies.longestDwell')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sortingStrategy.strategies.latestDeparture')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sortingStrategy.strategies.hybrid')}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "grey.400",
                  }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.sortingStrategy.example')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.title')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.description')}
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.sizes.verySmall')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.sizes.small')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.sizes.midsize')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.sizes.superMidsize')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.sizes.large')}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  <strong>{t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.spotPolicies')}</strong>
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.policies.universal')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.policies.largeOnly')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.policies.commonJets')}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "grey.400",
                  }}
                >
                  {t('screens.ramp.temporalTest.index.parameterGuide.sizeCategories.example')}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {result && (
          <>
            <PlacementList
              placement={result.placement}
              aircraft={result.aircraft}
              spots={result.spots}
              zones={result.zones}
            />

            <TimelineView
              spots={result.spots}
              aircraft={result.aircraft}
              placement={result.placement}
              log={result.log}
              horizonHours={result.horizonHours}
              startTime={new Date(result.startTime)}
            />

            {/* Decision Log */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('screens.ramp.temporalTest.index.decisionLog', { count: result.log.length })}
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                {result.aircraft.map((ac) => {
                  const acLogs = result.log.filter(
                    (e) => e.aircraftId === ac.id
                  );
                  if (acLogs.length === 0) return null;

                  return (
                    <Box key={ac.id} sx={{ mb: 2 }}>
                      <DecisionLogDisplay aircraft={ac} log={result.log} />
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </>
        )}
      </Stack>
    </Container>
  );
};
